import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { FilterQueryProps } from '../../../../global/filter/FilterQueryModal'
import { useFormatNumber } from '../../../../hooks/useFormatNumber'
import { useAccountReadContext } from '../../../../utils/AccountContextContext'
import { useApiClient } from '../../../../utils/ApiClient'
import { ClientApiClient } from '../../../../utils/clientApi'
import { useCallbackWithForceRefresh } from '../../../../utils/useCallbackWithForceRefresh'
import { useFetchOne } from '../../../../utils/useFetch'
import { SummaryItem } from '../../RebatesSummary'

interface IBRebateApprovalsSummary {
  summaryItems: SummaryItem[]
  forceRefresh: () => void
  isLoading: boolean
}

export const useIBRebateApprovalsSummary = (
  search?: FilterQueryProps
): IBRebateApprovalsSummary => {
  const apiClient = useApiClient(ClientApiClient)
  const { account } = useAccountReadContext()
  const { formatMoney } = useFormatNumber()
  const { t } = useTranslation()

  const { callback, forceRefresh } = useCallbackWithForceRefresh(async () => {
    if (!account?.clientIntroducingBroker?.id) {
      return null
    }

    return await apiClient.getRebateApprovalSummary(account.clientIntroducingBroker.id, { search })
  }, [account?.clientIntroducingBroker?.id, apiClient, search])

  const { data: summaryData, isLoading } = useFetchOne(callback)

  const summaryItems = useMemo<SummaryItem[]>(() => {
    return [
      {
        title: t('IB.RebatesPage.ApprovalsPage.Your Commission'),
        value: formatMoney(
          summaryData?.approvalsTotalCommission ?? 0,
          'USD',
          (summaryData?.approvalsTotalCommission || 0) > 0 ? 2 : 0
        ),
      },
      {
        title: t('IB.RebatesPage.ApprovalsPage.Rebates to Approve'),
        value: summaryData?.rebatesToApprove || 0,
      },
      {
        title: t('IB.RebatesPage.ApprovalsPage.Rebates Paid'),
        value: summaryData?.paidRebates || 0,
      },
    ]
  }, [summaryData, t])

  return { summaryItems, forceRefresh, isLoading }
}
